/**
 * This browser's identity.
 *
 * The device id is NOT a secret - it goes in the QR, and the server records it
 * against the link request. It does need to be stable: once the crypto core
 * lands this is the id the Olm account and the key directory hang off, and a
 * browser that regenerated it on every visit would look like a new device
 * every time and collect a fresh set of room keys it did not need.
 *
 * localStorage rather than sessionStorage for exactly that reason: unlike the
 * token, this is meant to outlive the tab.
 */
const KEY = 'nsibi.device_id.v1';

export function getDeviceId(): string {
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const fresh = crypto.randomUUID();
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // Private mode or storage disabled. A per-session id still links fine; it
    // just will not be recognised as the same browser next time.
    return crypto.randomUUID();
  }
}

/**
 * A label for the approval prompt on the phone, so the person can see what
 * they are authorising rather than approving an anonymous request.
 *
 * Parsed from the user agent, which is unreliable by nature, so every branch
 * falls through to something truthful rather than guessing.
 */
export function describeThisBrowser(): string {
  const ua = navigator.userAgent;

  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /OPR\//.test(ua)
      ? 'Opera'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Chrome\//.test(ua)
          ? 'Chrome'
          : /Safari\//.test(ua)
            ? 'Safari'
            : 'Browser';

  const os = /Android/.test(ua)
    ? 'Android'
    : /iPhone|iPad|iPod/.test(ua)
      ? 'iOS'
      : /Mac OS X/.test(ua)
        ? 'macOS'
        : /Windows/.test(ua)
          ? 'Windows'
          : /Linux/.test(ua)
            ? 'Linux'
            : '';

  return os ? `${browser} on ${os}` : browser;
}


/**
 * Tell the server this browser is signing out.
 *
 * Clearing the tab's session is not enough: the link row and this device's
 * keys stay in the directory, so the phone keeps listing the browser under
 * Linked devices and senders keep fanning room keys to a device that will
 * never read them again. This drops the CALLER'S OWN device (the server takes
 * the device id from the JWT, not from us), which is what makes "log out on
 * the web" show up on the phone.
 *
 * Best effort: a browser offline at sign-out still clears locally, and the
 * stale row is removable from the phone. Never throws - sign-out must always
 * complete.
 */
export async function unlinkThisBrowser(): Promise<void> {
  const { api } = await import('./api');
  try {
    await api('/v1/devices/self-unlink/', { method: 'POST', body: {} });
  } catch {
    /* offline or already gone - the phone can still remove the row */
  }
}

const CHAT_KEYS_RETIRED = 'nsibi.web_chat_keys_retired.v1';

/**
 * Take this browser's chat keys out of the directory, once, now that the web
 * has no chat (lib/features.ts). Until this runs, every sender keeps sealing
 * room keys to a browser that will never open them. Only for a browser that
 * ever made a device id; a 404 means it never published, which is done too.
 * Best effort and never throws: a browser offline today tries next visit.
 */
export async function retireWebChatKeys(): Promise<void> {
  let deviceId: string | null = null;
  try {
    if (localStorage.getItem(CHAT_KEYS_RETIRED)) return;
    deviceId = localStorage.getItem(KEY);
  } catch {
    return;
  }
  const { api, ApiError } = await import('./api');
  try {
    if (deviceId) {
      await api('/v1/keys/device/', { method: 'DELETE', body: { device_id: deviceId } });
    }
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 404) return;
  }
  try {
    localStorage.setItem(CHAT_KEYS_RETIRED, '1');
  } catch {
    /* nothing to remember it in; the next visit asks again and gets a 404 */
  }
}
